import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const TYPE_STYLE = {
  human: { color: '#16a34a', bg: '#f0fdf4', icon: '👤' },
  ai:    { color: '#7c3aed', bg: '#f5f3ff', icon: '✦' },
  system:{ color: '#2563eb', bg: '#eff6ff', icon: '●' },
};

function timeAgo(dateStr) {
  const d = new Date(dateStr.replace(' ', 'T'));
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function ProjectActivityTimeline({ projectId }) {
  const [events,      setEvents]      = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [total,       setTotal]       = useState(0);
  const [projectTitle,setProjectTitle]= useState('');
  const [isDeleted,   setIsDeleted]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState('');

  const fetchPage = useCallback(async (pageNum, append) => {
    if (!projectId) return;
    append ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
      const res = await api.get(`/gpf/v1/activity-log?project_id=${projectId}&page=${pageNum}`);
      setEvents(prev => append ? [...prev, ...res.data.events] : res.data.events);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
      setProjectTitle(res.data.project_title);
      setIsDeleted(res.data.is_deleted);
      if (!append && res.data.events.length > 0) setSelected(res.data.events[0]);
    } catch (e) {
      setError('Could not load activity for this project.');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setPage(1);
    fetchPage(1, false);
  }, [projectId, fetchPage]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, true);
  };

  if (!projectId) {
    return <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '40px 0' }}>Select a project above to see its activity timeline.</p>;
  }
  if (loading) return <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '40px 0' }}>Loading activity...</p>;
  if (error) return <p style={{ fontSize: 13, color: '#dc2626', textAlign: 'center', padding: '40px 0' }}>{error}</p>;
  if (events.length === 0) return <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '40px 0' }}>No activity recorded yet for this project.</p>;

  return (
    <div>
      {projectTitle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{projectTitle}</h3>
          {isDeleted && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '2px 9px', borderRadius: 99 }}>
              Project deleted
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, marginBottom: 16, fontSize: 12, color: '#64748b' }}>
        <span>{total} total event{total !== 1 ? 's' : ''}</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: TYPE_STYLE.system.color }} />Task</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: TYPE_STYLE.human.color }} />Human</span>
        <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: TYPE_STYLE.ai.color }} />AI Agent</span>
      </div>

      <div style={{ display: 'flex', gap: 0 }}>
        <div style={{ width: 40, flexShrink: 0, position: 'relative' }}>
          <div style={{ position: 'absolute', left: 19, top: 12, bottom: 12, width: 1.5, background: '#e2e8f0' }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {events.map(e => {
            const style = TYPE_STYLE[e.event_type === 'created' && e.actor_type === 'system' ? 'system' : e.actor_type] || TYPE_STYLE.human;
            const isSelected = selected?.id === e.id;
            return (
              <div
                key={e.id}
                onClick={() => setSelected(e)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', position: 'relative', height: 26, background: isSelected ? '#eff6ff' : 'transparent' }}
              >
                <div style={{ position: 'absolute', left: -21, width: 12, height: 12, borderRadius: '50%', background: style.color, border: '2px solid white', boxShadow: '0 0 0 1px #e2e8f0' }} />
                <span style={{ fontSize: 13, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{timeAgo(e.created_at)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {page < totalPages && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button style={styles.loadMoreBtn} onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : `Show older events (${total - events.length} more)`}
          </button>
        </div>
      )}

      {selected && (
        <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: (TYPE_STYLE[selected.actor_type] || TYPE_STYLE.human).bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 14 }}>{(TYPE_STYLE[selected.actor_type] || TYPE_STYLE.human).icon}</span>
            </div>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{selected.actor_name}</p>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                {selected.actor_type === 'ai' ? 'AI Agent' : selected.actor_type === 'system' ? 'System' : 'Team member'} · {new Date(selected.created_at.replace(' ', 'T')).toLocaleString()}
              </p>
            </div>
          </div>
          <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{selected.description}</p>
          {selected.task_title && (
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>On task: {selected.task_title}</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  legendItem: { display: 'flex', alignItems: 'center', gap: 5 },
  legendDot:  { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' },
  loadMoreBtn:{ padding: '8px 18px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151' },
};